import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Populates the free-shipping suggestion slider with products that help the shopper cross the
 * remaining free-shipping gap.
 *
 * The bar itself is server-rendered Liquid; this element owns only the suggestion picker + slider.
 * It fetches the live cart, seeds Shopify's complementary recommendations from the cart's
 * highest-value line (falling back to `related`), orders the candidates by how well they close
 * the gap, and injects them as a horizontal scroll-snap slider with a numbered pager. It re-runs
 * whenever the cart changes and hides once the threshold is met.
 *
 * The slider swipes natively on touch; `#bindDrag` adds mouse drag on top, and the pager's
 * arrows step one card at a time and disable themselves at either end.
 *
 * Recommendations are not price-aware, so ordering is best-effort (see `#orderCandidates`).
 *
 * @extends {Component}
 */
class FreeShippingSuggestions extends Component {
  /** Max cards to show in the slider. */
  #maxCards = 6;

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {IntersectionObserver | null} */
  #activeObserver = null;

  /** Cached candidate lists keyed by `${seedProductId}:${intent}`. @type {Map<string, Array>} */
  #recCache = new Map();

  #running = false;
  #pending = false;

  /** Index of the card currently in view; drives the active dot and the arrow states. */
  #activeIndex = 0;

  /** Live mouse-drag, or null when not dragging. @type {{startX: number, startScroll: number, moved: number, active: boolean} | null} */
  #drag = null;

  /** Set when a drag ends, so the click it synthesises never reaches a card's add button. */
  #swallowClick = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    this.#bindDrag();
    this.#refresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    this.#controller?.abort();
    this.#activeObserver?.disconnect();
  }

  /** @param {import('@shopify/events').CartLinesUpdateEvent} event */
  #onCartUpdate = (event) => {
    const promise = /** @type {any} */ (event)?.promise;
    // Wait for the mutation to settle so `/cart.js` reflects the new total.
    if (promise && typeof promise.then === 'function') {
      promise.then(() => this.#refresh()).catch(() => {});
    } else {
      this.#refresh();
    }
  };

  get #track() {
    return this.querySelector('.fs-suggestions__track');
  }

  get #dots() {
    return this.querySelector('.fs-suggestions__dots');
  }

  get #numbers() {
    return this.querySelector('.fs-suggestions__numbers');
  }

  get #reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  get #thresholdCents() {
    return parseInt(this.dataset.thresholdCents ?? '0', 10) || 0;
  }

  /** Intents to try in order: the configured one (complementary), then `related` as a fallback. */
  get #intents() {
    const primary = this.dataset.intent || 'complementary';
    return primary === 'related' ? ['related'] : [primary, 'related'];
  }

  async #refresh() {
    if (this.#running) {
      this.#pending = true;
      return;
    }
    this.#running = true;

    try {
      const cart = await this.#fetchCart();
      if (!cart || !cart.items?.length) return this.#hide();

      const remaining = this.#thresholdCents - cart.total_price;
      if (remaining <= 0) return this.#hide();

      const seedId = this.#pickSeed(cart);
      if (!seedId) return this.#hide();

      const cartProductIds = new Set(cart.items.map((item) => String(item.product_id)));

      // Try the configured intent (complementary) first; if it yields nothing usable,
      // fall back to algorithmic `related` so the block isn't empty for products with no
      // curated complements. (On a data-less dev store `related` may also be empty.)
      let pool = [];
      for (const intent of this.#intents) {
        const candidates = await this.#fetchCandidates(seedId, intent);
        pool = candidates.filter((candidate) => !cartProductIds.has(String(candidate.productId)));
        if (pool.length > 0) break;
      }

      const ordered = this.#orderCandidates(pool, remaining);
      if (ordered.length === 0) return this.#hide();

      this.#render(ordered);
    } catch (error) {
      if (/** @type {any} */ (error)?.name !== 'AbortError') {
        console.warn('[free-shipping-suggestions]', error);
        this.#hide();
      }
    } finally {
      this.#running = false;
      if (this.#pending) {
        this.#pending = false;
        this.#refresh();
      }
    }
  }

  /** @returns {Promise<{ total_price: number, items: Array } | null>} */
  async #fetchCart() {
    const base = /** @type {any} */ (window).Theme?.routes?.cart_url ?? '/cart';
    const response = await fetch(`${base}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) return null;
    return response.json();
  }

  /**
   * Seed complementary recs from the cart's highest-value line, matching the Liquid default.
   * @param {{ items: Array }} cart
   */
  #pickSeed(cart) {
    let seed = this.dataset.seedProductId ?? null;
    let best = -1;
    for (const item of cart.items) {
      const linePrice = item.line_price ?? item.final_line_price ?? 0;
      if (linePrice > best) {
        best = linePrice;
        seed = String(item.product_id);
      }
    }
    return seed;
  }

  /**
   * @param {string} seedId
   * @param {string} intent - `complementary` or `related`.
   * @returns {Promise<Array<{ productId: string, price: number, node: Element }>>}
   */
  async #fetchCandidates(seedId, intent) {
    const key = `${seedId}:${intent}`;
    const cached = this.#recCache.get(key);
    if (cached) return cached;

    const base = this.dataset.recommendationsUrl;
    const sectionId = this.dataset.sectionId;
    const url =
      `${base}?section_id=${encodeURIComponent(sectionId ?? '')}` +
      `&product_id=${encodeURIComponent(seedId)}` +
      `&intent=${encodeURIComponent(intent)}&limit=10`;

    this.#controller?.abort();
    this.#controller = new AbortController();

    const response = await fetch(url, { signal: this.#controller.signal });
    if (!response.ok) throw new Error(`Recommendations returned ${response.status}`);

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const cards = [...doc.querySelectorAll('[data-fs-product-id]')].map((el) => ({
      productId: /** @type {HTMLElement} */ (el).dataset.fsProductId ?? '',
      price: parseInt(/** @type {HTMLElement} */ (el).dataset.fsPrice ?? '0', 10) || 0,
      node: el,
    }));

    this.#recCache.set(key, cards);
    return cards;
  }

  /**
   * Order candidates by how well a single add closes the gap: products that individually clear
   * the remaining amount come first (cheapest unlock first — least spend to earn free shipping),
   * then the rest by price descending (a bigger add gets closest). Capped at `#maxCards`.
   *
   * @param {Array<{ price: number }>} pool
   * @param {number} remaining
   */
  #orderCandidates(pool, remaining) {
    const unlocks = pool.filter((c) => c.price >= remaining).sort((a, b) => a.price - b.price);
    const rest = pool.filter((c) => c.price < remaining).sort((a, b) => b.price - a.price);
    return [...unlocks, ...rest].slice(0, this.#maxCards);
  }

  // Visibility is CSS-driven: `.fs-suggestions:not(:has(.fs-suggestion))` collapses the block,
  // so populating or clearing the track is all that's needed — no attribute toggle for a cart
  // re-render to fight.

  /** @param {Array<{ node: Element }>} cards */
  #render(cards) {
    const track = this.#track;
    if (!track) return;

    track.replaceChildren(...cards.map((candidate) => candidate.node.cloneNode(true)));
    // Cheap no-op once bound. Covers the case where the element upgraded before its children
    // were parsed, so connectedCallback found no track to bind to.
    this.#bindDrag();
    this.#buildDots(cards.length);
    this.#observeActive();
  }

  /**
   * Builds the pager: a previous arrow, the numbers, a next arrow. The arrows are created here
   * rather than rendered in Liquid because this container carries `data-skip-subtree-update` and
   * is wholly owned by this component — anything the host rendered into it would be wiped by the
   * first `replaceChildren` anyway, and would lose its listeners to the next cart morph.
   *
   * @param {number} count
   */
  #buildDots(count) {
    const dots = this.#dots;
    if (!dots) return;

    const numbers = document.createElement('div');
    numbers.className = 'fs-suggestions__numbers';
    numbers.append(
      ...Array.from({ length: count }, (_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'fs-suggestions__dot';
        dot.textContent = String(i + 1).padStart(2, '0');
        if (i === 0) dot.setAttribute('aria-current', 'true');
        dot.addEventListener('click', () => this.#goTo(i));
        return dot;
      })
    );

    dots.replaceChildren(this.#makeArrow('prev'), numbers, this.#makeArrow('next'));
    this.#activeIndex = 0;
    this.#updateArrows();
  }

  /**
   * The arrows have no text — the glyph is a CSS mask — so the label is the only accessible
   * name they get. Strings come from the host's data attributes because Liquid's `t` filter
   * cannot be reached from here.
   *
   * @param {'prev' | 'next'} direction
   */
  #makeArrow(direction) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `fs-suggestions__arrow fs-suggestions__arrow--${direction}`;
    button.setAttribute(
      'aria-label',
      (direction === 'next' ? this.dataset.labelNext : this.dataset.labelPrev) || direction
    );
    button.addEventListener('click', () => {
      this.#goTo(this.#activeIndex + (direction === 'next' ? 1 : -1));
    });
    return button;
  }

  /**
   * An arrow is disabled at the end it cannot move toward. The CSS hides a disabled arrow
   * outright — at position 1 there is no previous swipe, so there is no control for one — and
   * `disabled` also takes it out of the tab order.
   */
  #updateArrows() {
    const total = this.#track?.children.length ?? 0;
    const prev = this.#dots?.querySelector('.fs-suggestions__arrow--prev');
    const next = this.#dots?.querySelector('.fs-suggestions__arrow--next');

    if (prev instanceof HTMLButtonElement) prev.disabled = this.#activeIndex <= 0;
    if (next instanceof HTMLButtonElement) next.disabled = this.#activeIndex >= total - 1;
  }

  /** @param {number} index */
  #goTo(index) {
    const track = this.#track;
    if (!track) return;

    // Clamped so the arrows can hand us activeIndex ± 1 without bounds-checking first.
    const target = Math.min(Math.max(index, 0), track.children.length - 1);
    const card = /** @type {HTMLElement | undefined} */ (track.children[target]);
    if (!card) return;

    // Scroll by the delta between the card and the track's left edge — robust regardless of
    // the card's offsetParent, and only touches the track's own horizontal scroll.
    const delta = card.getBoundingClientRect().left - track.getBoundingClientRect().left;
    track.scrollBy({ left: delta, behavior: this.#reducedMotion ? 'auto' : 'smooth' });
  }

  /** Keep the active dot in sync as the slider scrolls. */
  #observeActive() {
    this.#activeObserver?.disconnect();

    const track = this.#track;
    if (!track || track.children.length < 2) return;

    this.#activeObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            this.#setActiveDot([...track.children].indexOf(entry.target));
          }
        }
      },
      { root: track, threshold: 0.5 }
    );

    for (const card of track.children) this.#activeObserver.observe(card);
  }

  /** @param {number} index */
  #setActiveDot(index) {
    const numbers = this.#numbers;
    if (!numbers || index < 0) return;

    this.#activeIndex = index;

    [...numbers.children].forEach((dot, i) => {
      if (i === index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });

    this.#updateArrows();
  }

  /**
   * Drag-to-swipe with a mouse.
   *
   * Touch is deliberately untouched: the track is `overflow-x: auto` with `scroll-snap-type`, so
   * a finger already swipes it natively, with the platform's own momentum and snapping. Taking
   * that over would be strictly worse. A mouse has no such gesture — the track can only be
   * reached with a trackpad's horizontal scroll or the pager — so pointer drag is added for it
   * alone.
   *
   * Bound once, on the element that survives every re-render: `replaceChildren` swaps the
   * track's children, never the track itself, and the host marks it `data-skip-subtree-update`
   * so a cart morph leaves it alone too.
   */
  #bindDrag() {
    const track = this.#track;
    if (!track || track.dataset.dragBound === 'true') return;
    track.dataset.dragBound = 'true';

    track.addEventListener('pointerdown', this.#onPointerDown);
    track.addEventListener('pointermove', this.#onPointerMove);
    track.addEventListener('pointerup', this.#onPointerUp);
    track.addEventListener('pointercancel', this.#onPointerUp);
    // Capture phase: a drag that finishes over a card must not also press its add button.
    track.addEventListener('click', this.#onClick, true);
  }

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;

    const track = this.#track;
    if (!track || track.scrollWidth <= track.clientWidth) return;

    this.#drag = { startX: event.clientX, startScroll: track.scrollLeft, moved: 0, active: false };
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    const drag = this.#drag;
    const track = this.#track;
    if (!drag || !track) return;

    const dx = event.clientX - drag.startX;
    drag.moved = Math.abs(dx);

    if (!drag.active) {
      // A few pixels of slop, so a click on the add button stays a click.
      if (drag.moved < 5) return;
      drag.active = true;
      track.setPointerCapture?.(event.pointerId);
      // Mandatory snapping fights a live scrollLeft write — it keeps yanking the scroll back to
      // the nearest card mid-drag. Off while dragging, restored on release, which is also what
      // settles the slider onto a card.
      track.style.scrollSnapType = 'none';
    }

    event.preventDefault();
    track.scrollLeft = drag.startScroll - dx;
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    const drag = this.#drag;
    const track = this.#track;
    this.#drag = null;
    if (!drag?.active || !track) return;

    track.releasePointerCapture?.(event.pointerId);
    track.style.scrollSnapType = '';
    this.#swallowClick = true;
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!this.#swallowClick) return;
    this.#swallowClick = false;
    event.preventDefault();
    event.stopPropagation();
  };

  #hide() {
    this.#activeObserver?.disconnect();
    this.#track?.replaceChildren();
    this.#dots?.replaceChildren();
    this.#activeIndex = 0;
  }
}

if (!customElements.get('free-shipping-suggestions')) {
  customElements.define('free-shipping-suggestions', FreeShippingSuggestions);
}
